import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes, signOut, signIn, fetchAuthSession } from 'aws-amplify/auth';
import { getUser, getCompany, companiesByOwnerSub } from '../graphQL/queries';
import { generateClient } from 'aws-amplify/api';
import { Hub } from 'aws-amplify/utils';
import { trackEvent, setUserId, clearUserId } from '../utils/analytics';
import { clearCache as clearEntityCache } from '../utils/entityCache';
import {
  isFederatedAttributes, takeReturnPath, clearReturnPath, shouldRedirectToResume, resumeUrl,
} from './accountFlow';

const AuthContext = createContext();

export const PROFILE_OK = 'ok';
export const PROFILE_NONE = 'none';
export const PROFILE_DELETED = 'deleted';
export const PROFILE_ERROR = 'error';
export const NEEDS_PROFILE = 'needs_profile';

const MAX_LOGIN_ATTEMPTS = 5;
const LOGIN_LOCKOUT_MS = 60000;
let loginAttempts = 0;
let lockoutUntil = 0;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState('');
  const [userID, setUserID] = useState('');
  const [userType, setUserType] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingProfile, setPendingProfile] = useState(null);
  const quietSignIn = useRef(false);
  const client = useMemo(
    () => generateClient(),
    []
  );
  const navigate = useNavigate();

  const clearProfileState = () => {
    setUser('');
    setUserID('');
    setUserType('');
  };

  const fetchUserData = async (authUser) => {
    const username = authUser.userId || authUser.username;
    let failed = false;

    const read = async (query, variables, pick) => {
      try {
        const res = await client.graphql({ query, variables });
        return pick(res.data);
      } catch (e) {
        const partial = e?.data ? pick(e.data) : null;
        if (!partial) failed = true;
        return partial;
      }
    };

    let profileData = await read(getUser, { id: username }, (d) => d?.getUser || null);
    let type = profileData ? 'User' : null;

    if (!profileData) {
      profileData = await read(getCompany, { id: username }, (d) => d?.getCompany || null);
      if (profileData) type = 'Company';
    }

    if (!profileData) {
      profileData = await read(companiesByOwnerSub, { ownerSub: username }, (d) => {
        const items = (d?.companiesByOwnerSub?.items || []).filter((c) => c && !c.isDeleted);
        return items.length > 0 ? items[0] : null;
      });
      if (profileData) type = 'Company';
    }

    if (profileData && profileData.isDeleted) return PROFILE_DELETED;
    if (profileData) {
      setUser({ ...authUser, ...profileData, type });
      setUserID(profileData.id);
      setUserType(type);
      setUserId(profileData.id);
      setPendingProfile(null);
      return PROFILE_OK;
    }
    return failed ? PROFILE_ERROR : PROFILE_NONE;
  };

  const markPending = (attributes) => {
    clearProfileState();
    setPendingProfile({
      email: attributes?.email || '',
      name: attributes?.name || '',
      federated: isFederatedAttributes(attributes),
    });
  };

  const checkAuthState = async (initial = true) => {
    try {
      const authUser = await getCurrentUser();
      if (!authUser) return;
      const attributes = await fetchUserAttributes();
      const status = await fetchUserData({ ...authUser, attributes });
      if (status === PROFILE_OK) {
        const back = takeReturnPath();
        if (back) navigate(back, { replace: true });
        return;
      }
      if (status === PROFILE_NONE) {
        clearReturnPath();
        markPending(attributes);
        if (shouldRedirectToResume(window.location.pathname)) {
          navigate(resumeUrl(), { replace: true });
        }
        return;
      }
      if (status === PROFILE_DELETED) {
        clearReturnPath();
        try { await signOut(); } catch (e) {}
        clearProfileState();
        setPendingProfile(null);
        return;
      }
      if (initial) clearProfileState();
    } catch (err) {
      if (initial) {
        clearProfileState();
        setPendingProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hubSubscription = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' && !quietSignIn.current) {
        checkAuthState(false);
      }
      if (payload.event === 'signInWithRedirect_failure') {
        clearReturnPath();
      }
    });
    return () => hubSubscription();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await fetchAuthSession();
        await checkAuthState(true);
      } catch (error) {
        setLoading(false);
      }
    })();
  }, []);

  const refreshProfile = async () => {
    try {
      const authUser = await getCurrentUser();
      const status = await fetchUserData(authUser);
      if (status === PROFILE_NONE) {
        markPending(await fetchUserAttributes());
      }
      return status;
    } catch (error) {
      return PROFILE_ERROR;
    }
  };

  const SignOut = async () => {
    clearEntityCache();
    clearReturnPath();
    setPendingProfile(null);
    try {
      trackEvent('sign_out');
      clearUserId();
      await signOut();
    } catch (error) {
    }
    clearProfileState();
  };

  const login = async (email, password) => {
    const now = Date.now();
    if (now < lockoutUntil) {
      const waitSec = Math.ceil((lockoutUntil - now) / 1000);
      throw new Error(`Too many login attempts. Please wait ${waitSec} seconds.`);
    }

    clearReturnPath();
    quietSignIn.current = true;
    try {
      try { await signOut(); } catch (e) {}
      const { nextStep } = await signIn({ username: email, password });
      if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        const unconfirmedError = new Error('Please confirm your email before signing in.');
        unconfirmedError.name = 'UserNotConfirmedException';
        throw unconfirmedError;
      }
      loginAttempts = 0;
      const authUser = await getCurrentUser();
      const status = await fetchUserData(authUser);
      if (status === PROFILE_OK) {
        trackEvent('sign_in', { metadata: { method: 'email' } });
        return true;
      }
      if (status === PROFILE_NONE) {
        markPending(await fetchUserAttributes());
        return NEEDS_PROFILE;
      }
      if (status === PROFILE_DELETED) {
        try { await signOut(); } catch (e) {}
        clearProfileState();
        const deletedError = new Error('This account was deleted.');
        deletedError.name = 'AccountDeletedError';
        throw deletedError;
      }
      return false;
    } catch (error) {
      if (error?.name === 'UserNotConfirmedException' || error?.name === 'AccountDeletedError') throw error;
      loginAttempts++;
      if (loginAttempts >= MAX_LOGIN_ATTEMPTS) {
        const backoff = Math.min(LOGIN_LOCKOUT_MS * Math.pow(2, loginAttempts - MAX_LOGIN_ATTEMPTS), 300000);
        lockoutUntil = Date.now() + backoff;
      }
      throw error;
    } finally {
      quietSignIn.current = false;
    }
  };

  const isOwnProfile = (profileUser) => {
    if (userType === 'Company') {
      return user && user.companyUser === profileUser;
    } else {
      return user && user.user === profileUser;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      SignOut,
      userID,
      userType,
      isOwnProfile,
      loading,
      pendingProfile,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
